import { WSQueryKey } from "@ws-react-query";
import { queriesPayeeEngagements } from "./queriesPayeeEngagements";
import { createV3Queries } from "@query/utils/createV3Queries";
import { requirementsApi } from "@api";
import { getRequirementsDependencies } from "../../modules/Requirements/dependencies";
import { QUERY_PAYEE } from "../../query/payee/keys";
import { QUERY_PAYEE_ROWS } from "../../query/search/payee/queries/useQueryPayeeRows";
import { QUERY_PAYEE_ROWS_SUMMARY } from "../../query/search/payee/queries/useQueryPayeeRowsSummary";

/**
 * Every lifecycle verb changes the instance's status/expiration and therefore
 * carries the same fan-out the retired v1 requirement mutations declared: the
 * requirement/data-source reads (`getRequirementsDependencies()`, lazy — see
 * that module) plus the payee surfaces that render requirement rollups
 * (contractor rows/summary, payee detail, engagement list sizes — still
 * v1-keyed during coexistence). `awaitDependencies` refetches the instance
 * detail before the mutating modal resolves, reproducing the v1 behavior.
 * The v1 reset omitted the fan-out (stale lists after a re-request) — fixed
 * by sharing this config.
 */
const requirementVerbConfig = (queries: {
  getPayeeRequirement: {
    getQueryKey: (request: { requirementId: string }) => WSQueryKey;
  };
}) => ({
  dependencies: () => [
    ...getRequirementsDependencies(),
    queriesPayeeEngagements.listPayeeEngagementsByPayer.queryKeyBase,
    QUERY_PAYEE,
    QUERY_PAYEE_ROWS,
    QUERY_PAYEE_ROWS_SUMMARY
  ],
  awaitDependencies: (
    _response: unknown,
    request: { requirementId: string }
  ) => [
    queries.getPayeeRequirement.getQueryKey({
      requirementId: request.requirementId
    })
  ]
});

/**
 * Payee requirement *instance* registry — mirrors `RequirementsApi` 1:1
 * (`/v3/onboarding/payee-requirements`). Instances are system-managed: they
 * materialize from a definition attached to an engagement, so there is no
 * create/delete here — only the review/override lifecycle verbs plus the
 * annotation-only `update` (`metadata`/`externalId`).
 */
export const queriesRequirements = createV3Queries(
  "requirementsApi",
  requirementsApi,
  {
    updatePayeeRequirement: requirementVerbConfig,
    approvePayeeRequirement: requirementVerbConfig,
    rejectPayeeRequirement: requirementVerbConfig,
    revertPayeeRequirement: requirementVerbConfig,
    extendPayeeRequirementExpiration: requirementVerbConfig,
    renewPayeeRequirement: requirementVerbConfig,
    resetPayeeRequirement: requirementVerbConfig,
    fulfillPayeeRequirement: requirementVerbConfig
  }
);
